import { ThemeProvider } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { InspectionFormTheme } from '@monorepo/old-web/js/components/themes/inspectionFormTheme';
import { FormSubmissionContainer } from '@monorepo/old-web/js/components/views/formSubmissionEditor/FormSubmissionContainer';
import { FormSubmissionEditorMode } from '@monorepo/old-web/js/components/views/formSubmissionEditor/FormSubmissionEditorMode';
import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import { UserFriendlyErrorMessage } from '@monorepo/shared/componentsV2/errorDisplays/UserFriendlyErrorMessage';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { BaseModal } from '@monorepo/shared/componentsV2/modals/BaseModal';
import { useCurrentUser } from '@monorepo/shared/hooks/useCurrentUser';
import { useProject } from '@monorepo/shared/hooks/useProject';
import { generateFormSlug } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { FallbackProps } from 'react-error-boundary';
import styled from 'styled-components';
import { useFormTemplateContext } from '../../EditFormTemplatePage/FormTemplateContext';
import { PreviewProjectInfo } from './PreviewProjectInfo';
import { useFormPreview } from './useFormPreview';
import { useSvgSprite } from './useSvgSprite';

const noop = () => null;
const asyncNoop = async () => null;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  .project-name {
    font-size: 26px !important;
    font-weight: 500;
  }

  .main-title {
    line-height: 1.2;
  }

  fieldset {
    min-width: 0;
    padding: 0;
    margin: 0;
    border: 0;
  }
`;

const ErrorContainer = styled.div`
  padding: 1rem;
`;

function PreviewError({ error }: FallbackProps) {
  return (
    <ErrorContainer>
      <Alert severity="error">
        <UserFriendlyErrorMessage
          error={error}
          prependedText="The form preview could not be generated."
        />
      </Alert>
    </ErrorContainer>
  );
}

export type FormPreviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  projectId?: string;
  setProjectId: (projectId: string) => void;
};

function PreviewFormSubmissionContainer({
  organizationId,
  projectId,
}: {
  projectId?: string;
  organizationId: string;
}) {
  const { formTemplate: formTemplateState } = useFormTemplateContext();

  const { isLoading: isLoadingSvgSprite } = useSvgSprite();

  const { currentUser, isLoading: isLoadingUser } = useCurrentUser();

  const {
    formSubmissionTemplate: formTemplate = {},
    isLoading: isLoadingFormPreview,
  } = useFormPreview({
    organizationId,
    projectId,
    formTemplate: formTemplateState,
  });

  const { project, isLoading: isLoadingProject } = useProject(
    projectId ? { variables: { projectId } } : { config: { enabled: false } },
  );

  const getFormData = useCallback(
    async () => ({ formTemplate, currentUser, project }),
    [formTemplate, currentUser, project],
  );

  const randomSlug = useMemo(() => generateFormSlug(), []);

  const isProjectSelectedByUserOrDefault = !!projectId;
  const isLoading =
    isLoadingSvgSprite ||
    isLoadingUser ||
    isLoadingFormPreview ||
    isLoadingProject ||
    !isProjectSelectedByUserOrDefault;

  if (isLoading) {
    return <Loading />;
  }

  return (
    <FormSubmissionContainer
      checkIsGroupComplete={asyncNoop}
      loadFormData={getFormData}
      mode={FormSubmissionEditorMode.PREVIEW}
      onCloseForm={noop}
      onDeleteSubmission={asyncNoop}
      onDownloadForm={asyncNoop}
      onReleaseLock={asyncNoop}
      onRequestLock={asyncNoop}
      onSaveSubmission={asyncNoop}
      projectId={projectId}
      setMode={noop}
      submissionSlug={randomSlug}
    />
  );
}

function FormPreviewModalContent({
  organizationId,
  projectId,
  setProjectId,
}: Omit<FormPreviewModalProps, 'isOpen' | 'onClose'>) {
  // force remounting when project switches
  const projectKey = `form-preview-for-${projectId}`;

  return (
    <FlexContainer>
      <PreviewProjectInfo
        organizationId={organizationId}
        projectId={projectId}
        setProjectId={setProjectId}
      />
      <ErrorBoundary Fallback={PreviewError} key={projectKey}>
        <PreviewFormSubmissionContainer
          key={projectKey}
          organizationId={organizationId}
          projectId={projectId}
        />
      </ErrorBoundary>
    </FlexContainer>
  );
}

export function FormPreviewModal(props: FormPreviewModalProps) {
  const { isOpen, onClose, ...rest } = props;

  return (
    <BaseModal
      title="Form Preview"
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullHeight
      showCloseButton
      unpaddedContent
    >
      <ErrorBoundary Fallback={PreviewError}>
        {/* Ensures the date/time pickers are all green instead of a weird mix of green+default colors */}
        <ThemeProvider theme={InspectionFormTheme}>
          <FormPreviewModalContent {...rest} />
        </ThemeProvider>
      </ErrorBoundary>
    </BaseModal>
  );
}
